import React, { useState, useEffect } from 'react';
import { useToast } from './ToastContainer';
import './AdminAssets.css'; // Inherits the existing .modal, .modal-overlay stales!

const AssetEditModal = ({ asset, onClose, onSave }) => {
  const { showSuccess, showError } = useToast();
  
  const [descriptionValue, setDescriptionValue] = useState(asset?.description || '');
  const [tagsValue, setTagsValue] = useState(asset?.tags || '');
  const [isActive, setIsActive] = useState(asset?.is_active ?? true);
  const [isPublic, setIsPublic] = useState(asset?.is_public ?? true);
  const [featured, setFeatured] = useState(asset?.featured ?? false);
  const [saving, setSaving] = useState(false);
  const [newFile, setNewFile] = useState(null);

  const [abcFields, setAbcFields] = useState({
    rhythm_style: '',
    key: '',
    composer_arranger: '',
    origin_region: '',
    source_album: '',
    tune_title: ''
  });

  useEffect(() => {
    if (asset && asset.category === 'ABC-Tune') {
      let isParsed = false;
      if (asset.description) {
        // First check for new format [ABC_METADATA]...[/ABC_METADATA]
        const metadataMatch = asset.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
        if (metadataMatch && metadataMatch[1]) {
          try {
            const metadataJson = JSON.parse(metadataMatch[1]);
            setAbcFields({
              rhythm_style: metadataJson.rhythm_style || '',
              key: metadataJson.key || '',
              composer_arranger: metadataJson.composer_arranger || '',
              origin_region: metadataJson.origin_region || '',
              source_album: metadataJson.source_album || '',
              tune_title: metadataJson.tune_title || ''
            });
            // Set description to everything outside the tags
            setDescriptionValue(asset.description.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim());
            isParsed = true;
          } catch (e) { console.error(e); }
        } else {
          try {
            const metadata = JSON.parse(asset.description);
            if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
              setAbcFields({
                rhythm_style: metadata.rhythm_style || '',
                key: metadata.key || '',
                composer_arranger: metadata.composer_arranger || '',
                origin_region: metadata.origin_region || '',
                source_album: metadata.source_album || '',
                tune_title: metadata.tune_title || ''
              });
              setDescriptionValue('');
              isParsed = true;
            }
          } catch (e) {
             // Not raw JSON
          }
        }
      }
      
      if (!isParsed) {
        setDescriptionValue(asset.description || '');
      }
    }
  }, [asset]);

  const handleAutoPopulateTags = () => {
    if (asset?.category !== 'ABC-Tune') return;
    const tags = ['abc'];
    if (abcFields.rhythm_style) tags.push(abcFields.rhythm_style.toLowerCase());
    if (abcFields.key) {
      let keyVal = abcFields.key.toLowerCase();
      if (!keyVal.startsWith('key ')) keyVal = `key ${keyVal}`;
      tags.push(keyVal);
    }
    if (abcFields.composer_arranger && ['paul stewart cranford', 'paul cranford', 'p.s.c.', 'psc', 'cranford'].some(term => abcFields.composer_arranger.toLowerCase().includes(term))) {
      tags.push('paul stewart cranford (p.s.c.)');
    }
    setTagsValue(tags.join(', '));
  };

  if (!asset) return null;

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let finalDescription = descriptionValue || '';
      if (asset.category === 'ABC-Tune') {
        const abcMetadata = {
          rhythm_style: abcFields.rhythm_style,
          key: abcFields.key,
          composer_arranger: abcFields.composer_arranger,
          origin_region: abcFields.origin_region,
          source_album: abcFields.source_album,
          tune_title: abcFields.tune_title
        };

        // Clean existing markdown tag to prevent duplicate injections natively
        let cleanDesc = finalDescription.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim();

        // Ensure string builder identically matches what the server route expects natively
        finalDescription = cleanDesc
          ? `${cleanDesc}\n\n[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`
          : `[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`;
      }

      // If a new replacement file was selected, upload that first natively mapping /api/assets/:id/file
      if (newFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', newFile);

        const fileResponse = await fetch(`/api/assets/${asset.id}/file`, {
          method: 'PUT',
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          },
          body: fileFormData
        });

        if (!fileResponse.ok) {
          const fileErrData = await fileResponse.json();
          throw new Error('File replacement error: ' + (fileErrData.error || fileErrData.message || 'failed to update binary block'));
        }
      }

      const response = await fetch(`/api/assets/${asset.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: JSON.stringify({
          category: asset.category || '',
          subcategory: asset.subcategory || '',
          description: finalDescription || '',
          alt_text: asset.alt_text || '',
          tags: tagsValue || '',
          is_public: isPublic,
          is_active: isActive,
          featured: featured
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        if (errData.errors) {
          throw new Error('Validation: ' + errData.errors.map(e => `${e.path || e.param}: ${e.msg}`).join(', '));
        }
        throw new Error(errData.error || 'Failed to update asset metadata');
      }

      showSuccess('Asset updated successfully');
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error(err);
      showError('Failed to update asset: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Asset #{asset.id}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleUpdate} className="edit-form">
          <div className="form-group edit-preview">
            <p className="file-info">
              <strong>Asset ID:</strong> {asset.id} | <strong>File:</strong> {asset.original_filename}
            </p>
          </div>

          <div className="form-group checkbox-group" style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'var(--color-bg-alt)', borderRadius: '4px' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                style={{ marginRight: '10px', width: 'auto' }}
              />
              ⭐ Featured Content
            </label>
            <small style={{ display: 'block', marginLeft: '25px', color: 'var(--color-text-secondary)' }}>
              Pin this asset to the top of grids and lists
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              Tags (comma separated):
              {asset.category === 'ABC-Tune' && (
                <button
                  type="button"
                  onClick={handleAutoPopulateTags}
                  style={{
                    marginLeft: '10px',
                    padding: '4px 12px',
                    fontSize: '12px',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title="Auto-populate tags from ABC fields"
                >
                  ✨ Auto-populate
                </button>
              )}
            </label>
            <input
              type="text"
              value={tagsValue}
              onChange={(e) => setTagsValue(e.target.value)}
              placeholder="e.g. reel, key G, traditional"
            />
          </div>

          {asset.category === 'ABC-Tune' && (
            <div className="abc-fields-container">
              <h4>ABC Metadata</h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>Tune Title (T:):</label>
                  <input
                    type="text"
                    value={abcFields.tune_title}
                    onChange={(e) => setAbcFields({...abcFields, tune_title: e.target.value})}
                  />
                </div>
                <div className="form-group half">
                  <label>Composer (C:):</label>
                  <input
                    type="text"
                    value={abcFields.composer_arranger}
                    onChange={(e) => setAbcFields({...abcFields, composer_arranger: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>Rhythm (R:):</label>
                  <input
                    type="text"
                    value={abcFields.rhythm_style}
                    onChange={(e) => setAbcFields({...abcFields, rhythm_style: e.target.value})}
                  />
                </div>
                <div className="form-group half">
                  <label>Key (K:):</label>
                  <input
                    type="text"
                    value={abcFields.key}
                    onChange={(e) => setAbcFields({...abcFields, key: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>Origin (O:):</label>
                  <input
                    type="text"
                    value={abcFields.origin_region}
                    onChange={(e) => setAbcFields({...abcFields, origin_region: e.target.value})}
                  />
                </div>
                <div className="form-group half">
                  <label>Album (S:):</label>
                  <input
                    type="text"
                    value={abcFields.source_album}
                    onChange={(e) => setAbcFields({...abcFields, source_album: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Description:</label>
            <textarea
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              rows="4"
            />
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label>Replace File (optional):</label>
            <input
              type="file"
              name="file"
              accept=".abc,.txt"
              onChange={(e) => setNewFile(e.target.files[0])}
            />
            <small className="form-text">
              Leave empty to keep current file.
            </small>
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  style={{ marginRight: '10px', width: 'auto' }}
                />
                Active
              </label>
            </div>
            <div className="form-group half">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  style={{ marginRight: '10px', width: 'auto' }}
                />
                Public
              </label>
            </div>
          </div>

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetEditModal;
